// frontend/src/components/BroksumFetchForm.jsx

import React, { useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";

export default function BroksumFetchForm() {
  const { token } = useAuth() || {};
  const [tradeDate, setTradeDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  const [investor, setInvestor] = useState("ALL"); // FOREIGN | LOCAL | ALL
  const [transactionType, setTransactionType] = useState("NET"); // NET | GROSS | ALL (filter backend)
  const [limitSymbols, setLimitSymbols] = useState(0); // 0=all

  const [loading, setLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState(""); // "fetch" | "repair" | ""
  const [msg, setMsg] = useState("");
  const [result, setResult] = useState(null);

  const canRun = useMemo(() => !!tradeDate && !loading, [tradeDate, loading]);

  const runJob = async (mode) => {
    if (!canRun) return;

    setLoading(true);
    setLoadingMode(mode);
    setMsg("");
    setResult(null);

    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const qs = new URLSearchParams({
        trade_date: tradeDate,
        investor,
        transaction_type: transactionType,
      });
      if (Number(limitSymbols) > 0) qs.set("limit_symbols", String(limitSymbols));

      const endpoint =
        mode === "repair"
          ? "/api/broksum/detail/repair-daily"
          : "/api/broksum/detail/fetch-daily";

      const res = await fetch(`${endpoint}?${qs.toString()}`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Request gagal");

      setResult(data);
      setMsg(mode === "repair" ? "Repair Broksum berhasil ✅" : "Fetch Broksum berhasil ✅");
    } catch (e) {
      setMsg(`Error: ${e.message}`);
    } finally {
      setLoading(false);
      setLoadingMode("");
    }
  };

  const handleRun = () => runJob("fetch");
  const handleRepair = () => runJob("repair");

  return (
    <div className="text-sm text-gray-100">
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="block text-xs font-medium mb-1">Trade Date</label>
            <input
              type="date"
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              className="w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Investor</label>
              <select
                value={investor}
                onChange={(e) => setInvestor(e.target.value)}
                className="w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-2 text-sm"
              >
                <option value="FOREIGN">FOREIGN</option>
                <option value="LOCAL">LOCAL</option>
                <option value="ALL">ALL</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Transaction Type</label>
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-2 text-sm"
              >
                <option value="NET">NET</option>
                <option value="GROSS">GROSS</option>
                <option value="ALL">ALL</option>
              </select>
              <p className="mt-1 text-[11px] text-gray-400">
                Ini hanya filter backend dari field JSON (bukan param GoAPI).
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Limit Symbols (testing)</label>
            <input
              type="number"
              min="0"
              value={limitSymbols}
              onChange={(e) => setLimitSymbols(e.target.value)}
              className="w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-2 text-sm"
              placeholder="0 = all symbols"
            />
            <p className="mt-1 text-[11px] text-gray-400">
              0 = tarik semua saham (token besar). Isi 5–20 untuk test.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={handleRun}
            disabled={!canRun}
            className={`w-full py-2 px-3 rounded-lg text-sm font-semibold
              ${
                !canRun
                  ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-500 text-white"
              }`}
          >
            {loading && loadingMode === "fetch" ? "Fetching Broksum..." : "Fetch Broksum (Daily)"}
          </button>

          <button
            onClick={handleRepair}
            disabled={!canRun}
            className={`w-full py-2 px-3 rounded-lg text-sm font-semibold
              ${
                !canRun
                  ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                  : "bg-sky-600 hover:bg-sky-500 text-white"
              }`}
          >
            {loading && loadingMode === "repair" ? "Repairing Missing..." : "Repair Missing (BUY/WATCH)"}
          </button>
        </div>

        {msg && <p className="text-xs whitespace-pre-line">{msg}</p>}

        {result && (
          <div className="mt-2 border-t border-gray-700 pt-2 text-xs">
            <div className="space-y-1">
              <div>
                <span className="text-gray-400">Trade Date:</span>{" "}
                <span className="font-mono">{result.trade_date || "-"}</span>
              </div>
              <div>
                <span className="text-gray-400">Snapshot:</span>{" "}
                <span className="font-mono">{result.snapshot_dt || "-"}</span>
              </div>

              {/* Daily mode: symbols_total; Repair mode: universe_total/missing_total */}
              <div>
                <span className="text-gray-400">Symbols total:</span>{" "}
                <span className="font-mono">
                  {result.symbols_total ?? result.universe_total ?? "-"}
                </span>
                {typeof result.missing_total !== "undefined" && (
                  <>
                    <span className="text-gray-400 ml-2">Missing:</span>{" "}
                    <span className="font-mono">{result.missing_total}</span>
                  </>
                )}
              </div>

              {typeof result.symbols_repaired !== "undefined" && (
                <div>
                  <span className="text-gray-400">Repaired symbols:</span>{" "}
                  <span className="font-mono">{result.symbols_repaired}</span>
                  <span className="text-gray-400 ml-2">Empty:</span>{" "}
                  <span className="font-mono">{result.symbols_empty ?? 0}</span>
                  <span className="text-gray-400 ml-2">Failed:</span>{" "}
                  <span className="font-mono">{result.symbols_failed ?? 0}</span>
                </div>
              )}

              <div>
                <span className="text-gray-400">Inserted:</span>{" "}
                <span className="font-mono">{result.rows?.inserted ?? "-"}</span>{" "}
                <span className="text-gray-400 ml-2">Updated:</span>{" "}
                <span className="font-mono">{result.rows?.updated ?? "-"}</span>{" "}
                <span className="text-gray-400 ml-2">Skipped:</span>{" "}
                <span className="font-mono">{result.rows?.skipped ?? "-"}</span>
              </div>

              {/* Daily endpoint lama kadang return failed_symbols sebagai list; UI lama treat as number.
                  Kita render aman: kalau array -> length, kalau number -> number */}
              <div>
                <span className="text-gray-400">Failed symbols:</span>{" "}
                <span className="font-mono">
                  {Array.isArray(result.failed_symbols)
                    ? result.failed_symbols.length
                    : result.failed_symbols ?? result.symbols_failed ?? 0}
                </span>
              </div>

              {/* Optional: show small preview list for repair */}
              {(Array.isArray(result.empty_symbols) && result.empty_symbols.length > 0) && (
                <div className="pt-1">
                  <span className="text-gray-400">Empty list (sample):</span>{" "}
                  <span className="font-mono">{result.empty_symbols.slice(0, 10).join(", ")}</span>
                </div>
              )}
              {(Array.isArray(result.failed_symbols) && result.failed_symbols.length > 0) && (
                <div className="pt-1">
                  <span className="text-gray-400">Failed list (sample):</span>{" "}
                  <span className="font-mono">{result.failed_symbols.slice(0, 10).join(", ")}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
